/**
 * Platform Section
 *
 * Platform/device pie charts + comparison table.
 */

import React from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Monitor, Smartphone, Tablet, Globe } from 'lucide-react';
import type { PlatformBreakdown } from '../../../types/analytics';
import { ChartSkeleton } from './AnalyticsSkeleton';

const PLATFORM_COLORS = ['#a855f7', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#6366f1'];
const DEVICE_COLORS = ['#3b82f6', '#a855f7', '#f59e0b', '#10b981'];

const DEVICE_ICONS: Record<string, React.ElementType> = {
  desktop: Monitor,
  mobile: Smartphone,
  tablet: Tablet,
};

interface PlatformSectionProps {
  platforms: PlatformBreakdown | null;
  loading: boolean;
}

export const PlatformSection: React.FC<PlatformSectionProps> = ({ platforms, loading }) => {
  if (loading || !platforms) {
    return (
      <div className="space-y-4">
        <ChartSkeleton />
        <ChartSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Platform Pie Chart */}
      {platforms.platforms.length > 0 && (
        <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">By Platform</h3>
          <div className="flex items-center gap-4">
            <ResponsiveContainer width={140} height={140}>
              <PieChart>
                <Pie
                  data={platforms.platforms}
                  dataKey="events"
                  nameKey="platform"
                  cx="50%"
                  cy="50%"
                  outerRadius={60}
                  paddingAngle={2}
                >
                  {platforms.platforms.map((_, idx) => (
                    <Cell key={idx} fill={PLATFORM_COLORS[idx % PLATFORM_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2 flex-1">
              {platforms.platforms.map((p, idx) => (
                <div key={p.platform} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: PLATFORM_COLORS[idx % PLATFORM_COLORS.length] }}
                    />
                    <span className="text-xs capitalize text-gray-600">{p.platform}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-bold text-gray-900">{p.events.toLocaleString()}</span>
                    <span className="text-[10px] text-gray-400 ml-1">events</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Device Pie Chart */}
      {platforms.devices.length > 0 && (
        <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">By Device</h3>
          <div className="flex items-center gap-4">
            <ResponsiveContainer width={140} height={140}>
              <PieChart>
                <Pie
                  data={platforms.devices}
                  dataKey="events"
                  nameKey="device"
                  cx="50%"
                  cy="50%"
                  outerRadius={60}
                  paddingAngle={2}
                >
                  {platforms.devices.map((_, idx) => (
                    <Cell key={idx} fill={DEVICE_COLORS[idx % DEVICE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-3 flex-1">
              {platforms.devices.map((d) => {
                const DeviceIcon = DEVICE_ICONS[d.device] || Globe;
                return (
                  <div key={d.device} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DeviceIcon className="w-4 h-4 text-gray-400" />
                      <span className="text-xs capitalize text-gray-600">{d.device}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-bold text-gray-900">{d.users}</span>
                      <span className="text-[10px] text-gray-400 ml-1">users</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Comparison Table */}
      <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm overflow-x-auto">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Platform Comparison</h3>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left py-2 font-semibold text-gray-500">Platform</th>
              <th className="text-right py-2 font-semibold text-gray-500">Events</th>
              <th className="text-right py-2 font-semibold text-gray-500">Users</th>
              <th className="text-right py-2 font-semibold text-gray-500">Sessions</th>
            </tr>
          </thead>
          <tbody>
            {platforms.platforms.map((p) => {
              const sessions = platforms.sessions_by_platform.find(
                (s) => s.platform === p.platform
              );
              return (
                <tr key={p.platform} className="border-b border-gray-50">
                  <td className="py-2 capitalize font-medium text-gray-700">{p.platform}</td>
                  <td className="py-2 text-right text-gray-600">{p.events.toLocaleString()}</td>
                  <td className="py-2 text-right text-gray-600">{p.users.toLocaleString()}</td>
                  <td className="py-2 text-right text-gray-600">
                    {(sessions?.sessions || 0).toLocaleString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
